/** Spatially clip "no work permitted" habitat lines against workable lines.
 *
 * A habitat line with no measure allowed in the selected months is a candidate
 * for the red dashed "no work" style. But different habitat lines (e.g. a
 * Bachmuschel potential-habitat line and a Helm line with measures) can run
 * along the SAME watercourse. Where a workable line already covers a stretch,
 * that stretch has permitted work and must NOT be dashed. This removes the
 * covered segments so the dashed lines are spatially mutually exclusive with
 * the coloured workable lines — leaving dashes only where there really is
 * species habitat with no permitted work.
 */

import type { GeoJSONFeature, GeoJSONFeatureCollection } from "../types";
import { extractLineParts } from "./geometry";

const M_PER_DEG_LAT = 111320;

interface XYSeg {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  minx: number;
  miny: number;
  maxx: number;
  maxy: number;
}

/** Squared distance (m²) from point p to segment a–b, all in local metres. */
function pointSegDistSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  const ex = px - cx;
  const ey = py - cy;
  return ex * ex + ey * ey;
}

export function clipNoWorkLines(
  candidates: GeoJSONFeatureCollection,
  workable: GeoJSONFeatureCollection,
  tolMeters = 12,
): GeoJSONFeatureCollection {
  const empty: GeoJSONFeatureCollection = { type: "FeatureCollection", features: [] };
  if (candidates.features.length === 0) return empty;

  // Local equirectangular projection around a reference latitude — accurate
  // enough over a single municipality (~10 km) and cheap.
  const sample =
    candidates.features[0] && extractLineParts(candidates.features[0].geometry)[0]?.[0];
  const lat0 = sample ? sample[1] : 48;
  const mPerDegLng = M_PER_DEG_LAT * Math.cos((lat0 * Math.PI) / 180);
  const toX = (lng: number) => lng * mPerDegLng;
  const toY = (lat: number) => lat * M_PER_DEG_LAT;

  // Precompute all workable segments in local metres with their bbox.
  const workSegs: XYSeg[] = [];
  for (const feature of workable.features) {
    for (const part of extractLineParts(feature.geometry)) {
      for (let i = 0; i < part.length - 1; i++) {
        const ax = toX(part[i][0]);
        const ay = toY(part[i][1]);
        const bx = toX(part[i + 1][0]);
        const by = toY(part[i + 1][1]);
        workSegs.push({
          ax,
          ay,
          bx,
          by,
          minx: Math.min(ax, bx),
          miny: Math.min(ay, by),
          maxx: Math.max(ax, bx),
          maxy: Math.max(ay, by),
        });
      }
    }
  }

  const tol2 = tolMeters * tolMeters;
  const out: GeoJSONFeature[] = [];

  for (const feature of candidates.features) {
    const parts = extractLineParts(feature.geometry);
    if (parts.length === 0) continue;

    // Candidate workable segments = those whose bbox is within tol of this
    // feature's bbox (cheap pre-filter before the per-segment distance test).
    let fMinX = Infinity;
    let fMinY = Infinity;
    let fMaxX = -Infinity;
    let fMaxY = -Infinity;
    for (const part of parts) {
      for (const [lng, lat] of part) {
        const x = toX(lng);
        const y = toY(lat);
        if (x < fMinX) fMinX = x;
        if (y < fMinY) fMinY = y;
        if (x > fMaxX) fMaxX = x;
        if (y > fMaxY) fMaxY = y;
      }
    }
    const near = workSegs.filter(
      (s) =>
        s.maxx >= fMinX - tolMeters &&
        s.minx <= fMaxX + tolMeters &&
        s.maxy >= fMinY - tolMeters &&
        s.miny <= fMaxY + tolMeters,
    );

    const keptParts: number[][][] = [];
    for (const part of parts) {
      let current: number[][] = [];
      for (let i = 0; i < part.length - 1; i++) {
        const a = part[i];
        const b = part[i + 1];
        const mx = toX((a[0] + b[0]) / 2);
        const my = toY((a[1] + b[1]) / 2);
        let covered = false;
        for (const s of near) {
          if (pointSegDistSq(mx, my, s.ax, s.ay, s.bx, s.by) <= tol2) {
            covered = true;
            break;
          }
        }
        if (covered) {
          if (current.length >= 2) keptParts.push(current);
          current = [];
        } else {
          if (current.length === 0) current.push(a);
          current.push(b);
        }
      }
      if (current.length >= 2) keptParts.push(current);
    }

    if (keptParts.length > 0) {
      out.push({
        type: "Feature",
        geometry:
          keptParts.length === 1
            ? { type: "LineString", coordinates: keptParts[0] }
            : { type: "MultiLineString", coordinates: keptParts },
        properties: feature.properties,
      });
    }
  }

  return { type: "FeatureCollection", features: out };
}
